/* eslint-disable max-lines-per-function */
import { Box3, Vector3 } from 'three';
import { getComponentModelFileCfn } from '@rta/rta-blueprint-library';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ThreeSceneEngine } from '../../engine';
import { ApplianceSceneBuilder } from '../../engine/builders';
import type { ApplianceInput } from '../../engine/builders';
import type { RenderApplianceProps } from '../../types/react-props';

type ApplianceDimensions = { width: number; depth: number; height: number };

type MeasuredAppliance = {
	dimensions: ApplianceDimensions;
	center: Vector3;
};

/**
 * Measure the appliance that was actually added to the scene.
 *
 * Returns null when nothing has been added yet or the geometry is degenerate,
 * in which case the caller falls back to the catalog's declared dimensions.
 */
const measureRenderedAppliance = (engine: ThreeSceneEngine): MeasuredAppliance | null => {
	const objectHolder = engine.sceneManager?.objectHolder;
	if (!objectHolder) return null;

	// Measure the appliance itself, not the scene: the holder also carries
	// presentation extras (the highlight circle, debug shapes) that would pad
	// the framing.
	const bounds = new Box3();
	objectHolder.children
		.filter((child) => child.userData?.type === 'appliance')
		.forEach((child) => bounds.union(new Box3().setFromObject(child)));
	if (bounds.isEmpty()) return null;

	const size = bounds.getSize(new Vector3());
	if (size.x <= 0 || size.y <= 0 || size.z <= 0) return null;

	return {
		dimensions: { width: size.x, height: size.y, depth: size.z },
		center: bounds.getCenter(new Vector3())
	};
};

/** Framing zoom when the controller does not ask for one: fill the viewer. */
const DEFAULT_APPLIANCE_ZOOM = 100;

/**
 * Aim and distance the camera at the appliance.
 *
 * Framed from the geometry that actually rendered: the declared size is the
 * catalog's cutout footprint — some SKUs carry zeroes, and a model can reach
 * past it (handles, hoods) — so measuring keeps both the distance and the
 * centre honest. Falls back to the declared size when nothing measurable was
 * added (a missing model, for instance).
 *
 * `zoom` is the controller's `zoom` percentage: 100 fills the viewer, and a
 * lower number pulls the camera back for more breathing room around the model.
 */
const frameAppliance = (engine: ThreeSceneEngine, declared: ApplianceDimensions, zoom: number): void => {
	const measured = measureRenderedAppliance(engine);

	engine.cameraManager?.setOptimalDistance(
		measured?.dimensions ?? declared,
		zoom,
		{ x: 0, y: 0 }, // offset
		{
			center: measured?.center,
			// Land on the framing immediately: this is a static preview, and an
			// eased move can stall when the demand-driven render loop idles out
			// mid-transition.
			animate: false
		}
	);
};

/**
 * The controller's framing zoom, guarded: the distance calculation divides by
 * it, so a zero or a negative would put the camera inside the model.
 */
const resolveZoom = (zoom: unknown): number => (
	typeof zoom === 'number' && Number.isFinite(zoom) && zoom > 0
		? Math.min(zoom, 400)
		: DEFAULT_APPLIANCE_ZOOM
);

/**
 * Render3DAppliance - Uses the new ThreeSceneEngine architecture.
 *
 * This component renders a single 3D appliance using the modular engine.
 */
export const Render3DAppliance = (props: RenderApplianceProps): React.ReactElement => {
	const ref = useRef<HTMLDivElement>(null);
	const engineRef = useRef<ThreeSceneEngine | null>(null);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);

	// Create a stable callback for setRendering
	const handleRenderingChange = useCallback((isRendering: boolean) => {
		try {
			props.controller?.setRendering?.(isRendering);
		} catch (error) {
			console.warn('Render3DAppliance: Error calling setRendering:', error);
		}
	}, [props.controller]);

	// Initialize engine on mount
	useEffect(() => {
		if (!ref.current) return;

		const container = ref.current;
		const quality = props.controller?.options?.renderQuality ?? 7;
		const configuredBaseUrl = props.controller?.options?.assetsBaseUrl;
		const basePath = configuredBaseUrl
			? (configuredBaseUrl.endsWith('/') ? configuredBaseUrl : `${configuredBaseUrl}/`)
			: 'https://web-assets.rtaoutdoorliving.com/assets/';

		// Create engine
		const engine = new ThreeSceneEngine({
			container,
			backgroundColor: props.backgroundColor || '#ffffff',
			quality: quality as any,
			xrEnabled: false,
			basePath,
			// A single appliance is framed by setOptimalDistance below. The
			// presentation auto-zoom is a kitchen behaviour — it animates the
			// camera along an arc that pivots at floor level, which would land
			// on the appliance's base and leave it sitting above the viewport's
			// centre with empty space beneath it.
			autoZoom: false
		});

		engine.initialize();
		engineRef.current = engine;

		// Create and register appliance builder
		const applianceBuilder = new ApplianceSceneBuilder({
			basePath,
			renderQuality: Math.abs(quality),
			qualitySettings: null,
			textureManager: engine.textureManager,
			modelManager: engine.modelManager
		});
		engine.registerBuilder('appliance', applianceBuilder);

		// Subscribe to render events
		engine.on('render:start', () => handleRenderingChange(true));
		engine.on('render:complete', () => handleRenderingChange(false));

		setIsInitialized(true);

		return () => {
			engine.dispose();
			engineRef.current = null;
			setIsInitialized(false);
		};
	}, []);

	// Build appliance when initialized and props change
	useEffect(() => {
		const engine = engineRef.current;
		if (!engine || !isInitialized || !props.component) return;

		// console.log('Render3DAppliance: Building appliance scene');

		const buildInput: ApplianceInput = {
			id: props.component.sku,
			sku: props.component.sku,
			modelPath: `models/${getComponentModelFileCfn(props.component)}`,
			size: {
				width: props.component.size.width,
				height: props.component.size.height,
				depth: props.component.size.depth
			},
			placement: props.component.placement,
			position: { x: 0, y: 0, z: 0 },
			rotation: { x: 0, y: 0, z: 0 }
		};

		engine.compose([{ builder: 'appliance', data: buildInput }])
			.then(() => {
				// console.log('Render3DAppliance: Appliance scene built');

				frameAppliance(engine, buildInput.size, resolveZoom(props.controller?.zoom));

				// Set up rebuild callback
				props.controller?.setRebuild?.(() => {
					// console.log('Render3DAppliance: Rebuild triggered');
					engine.rebuild([{ builder: 'appliance', data: buildInput }])
						.then(() => frameAppliance(engine, buildInput.size, resolveZoom(props.controller?.zoom)))
						.catch((error) => console.error('Render3DAppliance: Error rebuilding appliance:', error));
				});
			})
			.catch((error) => {
				console.error('Render3DAppliance: Error building appliance:', error);
			});
	}, [isInitialized, props.component]);

	return (
		<div
			ref={ref}
			style={{
				position: 'absolute',
				height: '100%',
				width: '100%',
				minHeight: '100px',
				minWidth: '100px'
			}}
		/>
	);
};
